// app/javascript/components/ColumnsConstructor/columnsAPI.js

// Получение CSRF токена
const getCSRFToken = () => {
  return document.querySelector('meta[name="csrf-token"]').content;
};

// Базовая функция для API запросов
const apiRequest = async (url, options = {}) => {
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-CSRF-Token': getCSRFToken(),
    },
  };

  const response = await fetch(url, {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Request failed with status ${response.status}: ${errorText}`);
  }

  return response.json();
};

// Загрузка контекста редактирования
export const fetchEditingContext = async () => {
  try {
    const data = await apiRequest('/api/statistic_columns/editing_context');
    return data.status === 'success' ? data.context : null;
  } catch (error) {
    console.error('Error fetching editing context:', error);
    return null;
  }
};

// Загрузка колонок с поддержкой персональных настроек
export const fetchColumns = async () => {
  try {
    const data = await apiRequest('/api/statistic_columns/metadata');
    return {
      columns: data.columns ? data.columns.filter(col => col.column_type !== 'chart_group') : [],
      userStatus: data.user_status || {}
    };
  } catch (error) {
    console.error('Error fetching columns:', error);
    return { columns: [], userStatus: {} };
  }
};

// Загрузка chart groups с поддержкой персональных настроек
export const fetchChartGroups = async () => {
  try {
    const data = await apiRequest('/api/statistic_columns/chart_groups');
    return data.chart_groups || [];
  } catch (error) {
    console.error('Error fetching chart groups:', error);
    return [];
  }
};

// Переключение на персональные настройки
export const switchToPersonalSettings = async () => {
  try {
    const data = await apiRequest('/api/personal_configuration/personalize', {
      method: 'POST'
    });
    return data.status === 'success';
  } catch (error) {
    console.error('Error switching to personal settings:', error);
    throw error;
  }
};

// Сброс к дефолтным настройкам
export const resetToDefaults = async () => {
  try {
    const data = await apiRequest('/api/personal_configuration/reset', {
      method: 'DELETE'
    });
    return data.status === 'success';
  } catch (error) {
    console.error('Error resetting to defaults:', error);
    throw error;
  }
};

// Определение правильного endpoint для админа/пользователя
const getColumnEndpoint = (userStatus, isEdit = false, columnId = null, columnKey = null) => {
  if (userStatus.is_admin) {
    // Админ всегда работает с системными настройками
    if (isEdit && columnId) {
      return {
        url: `/statistic_columns/${columnId}`,
        method: 'PATCH'
      };
    } else {
      return {
        url: '/statistic_columns',
        method: 'POST'
      };
    }
  } else {
    // Обычный пользователь ВСЕГДА работает с персональными настройками
    if (isEdit && columnKey) {
      return {
        url: `/api/personal_configuration/column/${columnKey}`,
        method: 'PUT'
      };
    } else {
      // ИСПРАВЛЕНИЕ: Создание новых колонок тоже через персональные настройки
      return {
        url: '/api/personal_configuration/add_column',
        method: 'POST'
      };
    }
  }
};

// Сохранение колонки
export const saveColumn = async (formData, editingColumn, userStatus) => {
  try {
    // Если пользователь не персонализировал настройки - персонализируем
    if (!userStatus.is_admin && !userStatus.is_personalized) {
      await switchToPersonalSettings();
    }

    const { url, method } = getColumnEndpoint(
      userStatus, 
      !!editingColumn, 
      editingColumn?.id, 
      editingColumn?.key
    );

    let requestBody;
    if (userStatus.is_admin) {
      // Админ всегда работает с системным API
      requestBody = {
        statistic_column: {
          ...formData,
          id: undefined
        }
      };
    } else {
      // Обычный пользователь всегда работает с персональным API
      requestBody = {
        column: {
          ...formData,
          id: undefined
        }
      };
    }

    await apiRequest(url, {
      method,
      body: JSON.stringify(requestBody)
    });

    return true;
  } catch (error) {
    console.error('Error saving column:', error);
    throw error;
  }
};

// Сохранение chart group
// Замените функцию saveChartGroup в app/javascript/components/ColumnsConstructor/columnsAPI.js

// Сохранение chart group
export const saveChartGroup = async (formData, editingChartGroup, userStatus) => {
  try {
    // Если пользователь не персонализировал настройки - персонализируем
    if (!userStatus.is_admin && !userStatus.is_personalized) {
      await switchToPersonalSettings();
    }

    let url, method, requestBody;

    if (userStatus.is_admin) {
      // Админ всегда работает с системным API
      if (editingChartGroup) {
        url = `/statistic_columns/${editingChartGroup.id}`;
        method = 'PATCH';
      } else {
        url = '/statistic_columns';
        method = 'POST';
      }
      
      requestBody = {
        statistic_column: {
          key: formData.key,
          label: formData.label,
          description: `Chart group: ${formData.group_name}`,
          column_type: 'chart_group',
          chart_color: formData.chart_color,
          is_visible: true,
          show_on_chart: true,
          calculation_config: {
            group_name: formData.group_name,
            metrics: formData.metrics,
            colors: formData.colors
          }
        }
      };
    } else {
      // Обычный пользователь работает с персональным API
      if (editingChartGroup) {
        // Редактирование через update_column
        url = `/api/personal_configuration/column/${editingChartGroup.key}`;
        method = 'PUT';
        requestBody = {
          column: {
            label: formData.label,
            description: `Chart group: ${formData.group_name}`,
            chart_color: formData.chart_color,
            calculation_config: {
              group_name: formData.group_name,
              metrics: formData.metrics,
              colors: formData.colors
            }
          }
        };
      } else {
        // Создание через add_chart_group
        url = '/api/personal_configuration/add_chart_group';
        method = 'POST';
        requestBody = {
          chart_group: {
            key: formData.key,
            label: formData.label,
            group_name: formData.group_name,
            chart_color: formData.chart_color,
            metrics: formData.metrics,
            colors: formData.colors
          }
        };
      }
    }

    await apiRequest(url, {
      method,
      body: JSON.stringify(requestBody)
    });

    return true;
  } catch (error) {
    console.error('Error saving chart group:', error);
    throw error;
  }
};

// Удаление колонки
export const deleteColumn = async (column, userStatus) => {
  try {
    // ИСПРАВЛЕНИЕ: Проверяем персонализацию для обычных пользователей
    if (!userStatus.is_admin && !userStatus.is_personalized) {
      // Для обычных пользователей без персональных настроек нужна персонализация
      const confirmed = confirm(
        'To remove this column from your view, you need personal settings. ' +
        'This will create your personal copy of all settings. Continue?'
      );
      if (!confirmed) {
        throw new Error('Operation cancelled by user');
      }
      
      // Создаем персональные настройки
      await switchToPersonalSettings();
    }
    
    let url;
    if (userStatus.is_admin && !userStatus.is_personalized) {
      // Админ БЕЗ персональных настроек удаляет из системы (для всех)
      url = `/statistic_columns/${column.id}`;
    } else {
      // Обычный пользователь ИЛИ админ с персональными настройками 
      // удаляет только из своих персональных настроек
      url = `/api/personal_configuration/column/${column.key}`;
    }

    await apiRequest(url, {
      method: 'DELETE'
    });

    return true;
  } catch (error) {
    console.error('Error deleting column:', error);
    throw error;
  }
};

// Удаление chart group
export const deleteChartGroup = async (chartGroup, userStatus) => {
  try {
    // ИСПРАВЛЕНИЕ: Проверяем персонализацию для обычных пользователей
    if (!userStatus.is_admin && !userStatus.is_personalized) {
      // Для обычных пользователей без персональных настроек нужна персонализация
      const confirmed = confirm(
        'To remove this chart group from your view, you need personal settings. ' +
        'This will create your personal copy of all settings. Continue?'
      );
      if (!confirmed) {
        throw new Error('Operation cancelled by user');
      }
      
      // Создаем персональные настройки
      await switchToPersonalSettings();
    }
    
    let url;
    if (userStatus.is_admin && !userStatus.is_personalized) {
      // Админ БЕЗ персональных настроек удаляет из системы (для всех)
      url = `/statistic_columns/${chartGroup.id}`;
    } else {
      // Обычный пользователь ИЛИ админ с персональными настройками 
      // удаляет только из своих персональных настроек
      url = `/api/personal_configuration/column/${chartGroup.key}`;
    }

    await apiRequest(url, {
      method: 'DELETE'
    });

    return true;
  } catch (error) {
    console.error('Error deleting chart group:', error);
    throw error;
  }
};

// Переключение видимости колонки
export const toggleColumnVisibility = async (column, userStatus) => {
  try {
    if (!userStatus.is_admin && !userStatus.is_personalized) {
      await switchToPersonalSettings();
    }

    let url;
    if (userStatus.is_admin) {
      // Админ работает с системными настройками
      url = `/statistic_columns/${column.id}`;
      await apiRequest(url, {
        method: 'PATCH',
        body: JSON.stringify({
          statistic_column: {
            is_visible: !column.is_visible
          }
        })
      });
    } else {
      // Пользователь работает с персональными настройками
      url = `/api/personal_configuration/toggle_column_visibility/${column.key}`;
      await apiRequest(url, {
        method: 'PUT'
      });
    }

    return true;
  } catch (error) {
    console.error('Error toggling column visibility:', error);
    throw error;
  }
};

// Переключение показа колонки на графике
export const toggleColumnChart = async (column, userStatus) => {
  try {
    if (!userStatus.is_admin && !userStatus.is_personalized) {
      await switchToPersonalSettings();
    }

    let url;
    if (userStatus.is_admin) {
      // Админ работает с системными настройками
      url = `/statistic_columns/${column.id}`;
      await apiRequest(url, {
        method: 'PATCH',
        body: JSON.stringify({
          statistic_column: {
            show_on_chart: !column.show_on_chart
          }
        })
      });
    } else {
      // Пользователь работает с персональными настройками
      url = `/api/personal_configuration/toggle_column_chart/${column.key}`;
      await apiRequest(url, {
        method: 'PUT'
      });
    }

    return true;
  } catch (error) {
    console.error('Error toggling column chart:', error);
    throw error;
  }
};

// Вспомогательная функция для добавления новой колонки в персональные настройки
const addNewColumnToPersonalSettings = async (formData, systemResponse) => {
  // Сначала убеждаемся что персональные настройки существуют
  try {
    await apiRequest('/api/personal_configuration/personalize', {
      method: 'POST'
    });
  } catch (error) {
    // Если настройки уже существуют, это нормально
    console.log('Personal settings already exist or created');
  }
  
  // Получаем текущие персональные настройки
  const currentSettingsResponse = await apiRequest('/api/personal_configuration/metadata');
  const currentColumns = currentSettingsResponse.columns || [];
  
  // Создаем объект новой колонки в формате персональных настроек
  const newColumn = {
    id: systemResponse?.id || Date.now(), // Используем ID из ответа или временный
    key: formData.key,
    label: formData.label,
    description: formData.description,
    column_type: formData.column_type,
    data_source: formData.data_source,
    calculation_config: formData.calculation_config || {},
    chart_color: formData.chart_color,
    show_on_chart: formData.show_on_chart,
    is_visible: formData.is_visible,
    is_percentage: ['percentage_ratio', 'percentage_attempts'].includes(formData.column_type),
    display_order: currentColumns.length,
    is_system: false,
    is_chart_group: false,
    group_name: null,
    group_metrics: null,
    group_colors: null,
    has_personal_changes: true,
    user_setting_id: systemResponse?.id || Date.now()
  };
  
  // Добавляем новую колонку в персональные настройки
  const updatedColumns = [...currentColumns, newColumn];
  
  await apiRequest('/api/personal_configuration', {
    method: 'PUT',
    body: JSON.stringify({
      configuration: {
        columns: updatedColumns
      }
    })
  });
};

// Вспомогательная функция для добавления новой chart group в персональные настройки
const addNewChartGroupToPersonalSettings = async (formData, systemResponse) => {
  // Сначала убеждаемся что персональные настройки существуют
  try {
    await apiRequest('/api/personal_configuration/personalize', {
      method: 'POST'
    });
  } catch (error) {
    // Если настройки уже существуют, это нормально
    console.log('Personal settings already exist or created');
  }
  
  // Получаем текущие персональные настройки
  const [columnsResponse, chartGroupsResponse] = await Promise.all([
    apiRequest('/api/personal_configuration/metadata'),
    apiRequest('/api/personal_configuration/chart_groups')
  ]);
  
  const currentColumns = columnsResponse.columns || [];
  const currentChartGroups = chartGroupsResponse.chart_groups || [];
  
  // Создаем объект новой chart group колонки
  const newColumn = {
    id: systemResponse?.id || Date.now(),
    key: formData.key,
    label: formData.label,
    description: `Chart group: ${formData.group_name}`,
    column_type: 'chart_group',
    data_source: null,
    calculation_config: {
      group_name: formData.group_name,
      metrics: formData.metrics,
      colors: formData.colors
    },
    chart_color: formData.chart_color,
    show_on_chart: true,
    is_visible: true,
    is_percentage: false,
    display_order: currentColumns.length,
    is_system: false,
    is_chart_group: true,
    group_name: formData.group_name,
    group_metrics: formData.metrics,
    group_colors: formData.colors,
    has_personal_changes: true,
    user_setting_id: systemResponse?.id || Date.now()
  };
  
  // Создаем объект новой chart group
  const newChartGroup = {
    id: systemResponse?.id || Date.now(),
    key: formData.key,
    label: formData.label,
    group_name: formData.group_name,
    metrics: formData.metrics,
    colors: formData.colors,
    chart_color: formData.chart_color
  };
  
  // Обновляем и колонки, и chart groups
  const updatedColumns = [...currentColumns, newColumn];
  const updatedChartGroups = [...currentChartGroups, newChartGroup];
  
  await apiRequest('/api/personal_configuration', {
    method: 'PUT',
    body: JSON.stringify({
      configuration: {
        columns: updatedColumns,
        chart_groups: updatedChartGroups
      }
    })
  });
};